// biome-ignore-all lint/suspicious/noConsole: SSE stream error reporting until Sentry SDK is wired up
import { extractSSEErrorMessage } from '@utils/extractSSEErrorMessage';
import { normalizeRole } from '@utils/roleUtils';
import { canonicalizeVideoUrl } from '@utils/videoParser';
import { useCallback, useEffect, useRef } from 'react';
import { API_BASE, SENTINEL_SSE_EVENTS, SSE_CONFIG } from 'src/constants/sentinel';
import {
	addOrUpdateTimelineEvent,
	addTimelineEvent,
	detectPhaseDataReceived,
	detectPhaseFailed,
	detectPhaseStarted,
	processCompleted,
	setDetectedKeyframes,
	updateSelectedClaims
} from 'src/store/slices/sentinel';
import { Role } from 'src/types/sentinel';
import { useAppDispatch, useAppSelector } from 'universal-scripts';

interface ActiveStepState {
	stepId: string;
	role: Role;
}

export function useStreamDetect(videoUrl: string | null, lang: string = 'en', refreshCache: boolean = false) {
	const dispatch = useAppDispatch();
	const processId = useAppSelector((state) => state.sentinel.processId);
	const status = useAppSelector((state) => state.sentinel.phases.detect.status);

	const eventSourceRef = useRef<EventSource | null>(null);
	const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const retryCountRef = useRef(0);

	const lastActiveStepRef = useRef<ActiveStepState | null>(null);
	const isCompletedRef = useRef(false);
	const hasStartedRef = useRef(false);

	const cleanup = useCallback(() => {
		if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
		if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);

		if (eventSourceRef.current) {
			eventSourceRef.current.close();
			eventSourceRef.current = null;
		}
	}, []);

	useEffect(() => {
		if (!videoUrl || !processId) return;

		if (status === 'completed' || status === 'error') return;

		if (!hasStartedRef.current) {
			hasStartedRef.current = true;
			if (status === 'idle') {
				dispatch(detectPhaseStarted());
				dispatch(addTimelineEvent({ phase: 'detect', eventType: 'phase_start' }));
			}
		}

		isCompletedRef.current = false;

		const connect = () => {
			if (isCompletedRef.current) return;

			// Send the canonical YouTube watch URL to the pipeline (strip list/index params, rewrite /shorts/),
			// while the original videoUrl is kept for display/orientation purposes.
			const url = `${API_BASE}/analyse-video?lang=${lang}&video_url=${encodeURIComponent(canonicalizeVideoUrl(videoUrl))}${refreshCache ? '&refresh_cache=true' : ''}`;

			try {
				const eventSource = new EventSource(url);
				eventSourceRef.current = eventSource;

				const resetConnectionTimeout = () => {
					if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
					connectionTimeoutRef.current = setTimeout(() => {
						if (!isCompletedRef.current) {
							cleanup();
							const msg = 'Connection timeout (server unresponsive)';
							dispatch(addTimelineEvent({ phase: 'detect', eventType: 'error', message: msg }));
							dispatch(detectPhaseFailed(msg));
						}
					}, SSE_CONFIG.CONNECTION_TIMEOUT_MS);
				};

				resetConnectionTimeout();

				eventSource.onopen = () => {
					retryCountRef.current = 0;
				};
				eventSource.addEventListener(SENTINEL_SSE_EVENTS.STATUS_CHANGE, (event) => {
					resetConnectionTimeout();
					try {
						const data = JSON.parse(event.data);
						const normalizedRole = normalizeRole(data.role) as Role;
						if (data.new_status === 'in_progress') {
							lastActiveStepRef.current = { stepId: data.step_id, role: normalizedRole };
						}

						dispatch(
							addOrUpdateTimelineEvent({
								phase: 'detect',
								eventType: 'step_update',
								stepId: data.step_id,
								stepStatus: data.new_status,
								role: normalizedRole
							})
						);
					} catch (error) {
						console.error('SSE Parse Error (status):', error);
					}
				});

				eventSource.addEventListener('video_keyframes', (event) => {
					resetConnectionTimeout();
					try {
						const data = JSON.parse(event.data);
						const keyframes = data.keyframe_remote_uris || data.image_urls;
						if (keyframes?.length) {
							dispatch(setDetectedKeyframes(keyframes));
						}
					} catch (error) {
						console.error('SSE Parse Error (keyframes):', error);
					}
				});

				eventSource.addEventListener(SENTINEL_SSE_EVENTS.VIDEO_ANALYSIS, (event) => {
					resetConnectionTimeout();
					try {
						const data = JSON.parse(event.data);
						dispatch(
							detectPhaseDataReceived({
								videoDescription: data.description,
								allClaims: data.claims,
								attacks: data.attacks,
								...(data.assets && { assets: data.assets })
							})
						);
					} catch (error) {
						console.error('SSE Parse Error (analysis):', error);
					}
				});

				eventSource.addEventListener(SENTINEL_SSE_EVENTS.EVALUATED_CLAIMS, (event) => {
					resetConnectionTimeout();
					try {
						const data = JSON.parse(event.data);
						const claims = data.claims || [];
						if (lastActiveStepRef.current) {
							dispatch(
								addOrUpdateTimelineEvent({
									phase: 'detect',
									eventType: 'step_update',
									stepId: lastActiveStepRef.current.stepId,
									stepStatus: 'completed',
									role: lastActiveStepRef.current.role
								})
							);
						}

						dispatch(updateSelectedClaims(claims));
						isCompletedRef.current = true;
						if (claims.length === 0) {
							dispatch(
								addTimelineEvent({
									phase: 'detect',
									eventType: 'phase_complete',
									message: 'No claims detected.'
								})
							);
							dispatch(processCompleted());
						} else {
							dispatch(addTimelineEvent({ phase: 'detect', eventType: 'phase_complete' }));
						}
						cleanup();
					} catch (error) {
						console.error('SSE Parse Error (claims):', error);
						cleanup();
					}
				});

				eventSource.addEventListener(SENTINEL_SSE_EVENTS.ERROR, (event) => {
					const errorMsg = extractSSEErrorMessage(event);
					isCompletedRef.current = true;
					dispatch(detectPhaseFailed(errorMsg));
					cleanup();
				});

				eventSource.onerror = () => {
					eventSource.close();
					if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
					if (isCompletedRef.current) return;
					if (retryCountRef.current < SSE_CONFIG.MAX_RETRIES) {
						retryCountRef.current++;
						const delay = SSE_CONFIG.RETRY_DELAY_MS * retryCountRef.current;

						retryTimeoutRef.current = setTimeout(() => {
							connect();
						}, delay);
					} else {
						dispatch(addTimelineEvent({ phase: 'detect', eventType: 'error', message: 'Connection lost' }));
						dispatch(detectPhaseFailed('Connection lost with server'));
					}
				};
			} catch (error) {
				console.error('SSE Init Failed:', error);
				dispatch(detectPhaseFailed('Failed to initialize connection'));
			}
		};

		connect();

		return () => {
			cleanup();
			hasStartedRef.current = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoUrl, processId, dispatch, cleanup, lang, refreshCache]);

	return { disconnect: cleanup };
}
